import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Globe, UserPlus, Rocket, Check, Sparkles, ChevronRight, ChevronLeft, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "business", label: "Business", Icon: Building2 },
  { key: "domain",   label: "Domain",   Icon: Globe },
  { key: "team",     label: "Team",     Icon: UserPlus },
  { key: "launch",   label: "Launch",   Icon: Rocket },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { user, updateProfile, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState({ business_name: user?.business_name || "", industry: "" });
  const [dom, setDom] = useState({ sender_domain: "", sender_email: "", sender_name: "" });
  const [invite, setInvite] = useState({ email: "", role: "member" });
  const [invitesSent, setInvitesSent] = useState([]);

  useEffect(() => { if (user?.business_name) setBiz(b => ({ ...b, business_name: user.business_name })); }, [user]);

  const finish = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me", { onboarding_complete: true });
      await refresh();
      toast.success("You're all set! Welcome to LaunchDeck.");
      nav("/");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const skipAll = async () => {
    if (!window.confirm("Skip setup? You can come back anytime from Settings.")) return;
    await finish();
  };

  const saveBusiness = async () => {
    if (!biz.business_name.trim()) { toast.error("Business name required"); return; }
    setBusy(true);
    try {
      await updateProfile({ business_name: biz.business_name.trim() });
      setStep(1);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const saveDomain = async () => {
    if (!dom.sender_domain.trim()) return setStep(2);  // allow skip
    setBusy(true);
    try {
      await api.post("/domain/setup", { ...dom, sender_name: dom.sender_name || biz.business_name });
      toast.success("Domain saved — DNS records ready in Settings → Sender Domain");
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid domain"); }
    finally { setBusy(false); }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      await api.post("/team/invite", invite);
      setInvitesSent(v => [...v, invite.email]);
      setInvite({ email: "", role: "member" });
      toast.success("Invitation sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const progress = (
    <div className="flex items-center justify-center gap-3 mb-8">
      {STEPS.map((s, i) => {
        const active = i === step, done = i < step;
        const Icon = s.Icon;
        return (
          <div key={s.key} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${active ? "shadow-lg scale-110" : ""}`} style={{ background: done || active ? "var(--brand)" : "var(--bg)", color: done || active ? "var(--on-brand)" : "var(--text-soft)", border: `2px solid ${done || active ? "var(--brand)" : "var(--border)"}` }}>
              {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
            </div>
            {i < STEPS.length - 1 && <div className="w-12 h-0.5 mx-2" style={{ background: done ? "var(--brand)" : "var(--border)" }} />}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }} data-testid="onboarding-page">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3 text-sm font-semibold" style={{ color: "var(--brand)" }}>
            <Sparkles className="w-4 h-4" /> WELCOME TO LAUNCHDECK
          </div>
          <h1 className="display text-4xl mb-2">Let&apos;s get your workspace ready</h1>
          <p className="text-sm" style={{ color: "var(--text-soft)" }}>Takes ~2 minutes. Everything is editable later in Settings.</p>
        </div>

        {progress}

        <Card className="p-8" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          {step === 0 && (
            <div data-testid="onboarding-step-business">
              <div className="flex items-center gap-2 mb-4"><Building2 className="w-5 h-5" style={{ color: "var(--brand)" }} /><h2 className="display text-2xl">Tell us about your business</h2></div>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>Used in your email footer, invite messages, and branding.</p>
              <div className="space-y-4">
                <div><Label>Business name *</Label><Input data-testid="onboard-biz-name" value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} placeholder="Acme Marketing" autoFocus /></div>
                <div><Label>Industry (optional)</Label><Input value={biz.industry} onChange={(e) => setBiz({ ...biz, industry: e.target.value })} placeholder="SaaS, ecommerce, agency…" /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-domain">
              <div className="flex items-center gap-2 mb-4"><Globe className="w-5 h-5" style={{ color: "var(--brand)" }} /><h2 className="display text-2xl">Sending domain (optional)</h2></div>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>
                Set your own domain now for better deliverability, or skip and use our default sender. You can add this anytime from Settings.
              </p>
              <div className="space-y-4">
                <div><Label>Domain</Label><Input data-testid="onboard-domain" value={dom.sender_domain} onChange={(e) => setDom({ ...dom, sender_domain: e.target.value })} placeholder="yourbrand.com" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>From email</Label><Input value={dom.sender_email} onChange={(e) => setDom({ ...dom, sender_email: e.target.value })} placeholder={`hello@${dom.sender_domain || "yourbrand.com"}`} /></div>
                  <div><Label>From name</Label><Input value={dom.sender_name} onChange={(e) => setDom({ ...dom, sender_name: e.target.value })} placeholder={biz.business_name || "Your Brand"} /></div>
                </div>
                <p className="text-xs p-3 rounded" style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)", color: "var(--text-soft)" }}>
                  💡 After saving, you&apos;ll get 4 DNS records to add at your registrar. Total setup: ~5 min.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-team">
              <div className="flex items-center gap-2 mb-4"><UserPlus className="w-5 h-5" style={{ color: "var(--brand)" }} /><h2 className="display text-2xl">Invite your team (optional)</h2></div>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>Invite teammates now, or add them later from Master Panel.</p>
              <div className="flex gap-2 items-end mb-4">
                <div className="flex-1"><Label>Email</Label><Input data-testid="onboard-invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="alex@company.com" /></div>
                <Button data-testid="onboard-invite-send" onClick={sendInvite} disabled={busy} className="text-white gap-1" style={{ background: "var(--brand)" }}><UserPlus className="w-4 h-4" /> Invite</Button>
              </div>
              {invitesSent.length > 0 && (
                <div className="space-y-1">
                  {invitesSent.map((e, i) => (
                    <div key={i} className="text-sm flex items-center gap-2 p-2 rounded" style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
                      <Check className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} /> Invitation sent to <strong>{e}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-launch" className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)" }}>
                <Rocket className="w-8 h-8" style={{ color: "var(--brand)" }} />
              </div>
              <h2 className="display text-3xl mb-2">You&apos;re ready to launch</h2>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>Your workspace is live. Here&apos;s what to try first:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-left">
                <ShortcutCard title="Import contacts" desc="CSV smart import auto-maps columns" href="/contacts" />
                <ShortcutCard title="Send first campaign" desc="AI newsletter → drag-drop preview" href="/campaigns" />
                <ShortcutCard title="Write a blog" desc="Generate with Gemini + hero image" href="/blog" />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
            <Button variant="ghost" onClick={skipAll} className="gap-1 text-xs" data-testid="skip-onboarding"><X className="w-3 h-3" /> Skip setup</Button>
            <div className="flex gap-2">
              {step > 0 && step < 3 && <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-1"><ChevronLeft className="w-4 h-4" /> Back</Button>}
              {step === 0 && <Button data-testid="onboard-next-0" onClick={saveBusiness} disabled={busy} className="text-white gap-1" style={{ background: "var(--brand)" }}>Continue {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}</Button>}
              {step === 1 && <Button data-testid="onboard-next-1" onClick={saveDomain} disabled={busy} className="text-white gap-1" style={{ background: "var(--brand)" }}>{dom.sender_domain ? "Save & continue" : "Skip domain"} {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}</Button>}
              {step === 2 && <Button data-testid="onboard-next-2" onClick={() => setStep(3)} className="text-white gap-1" style={{ background: "var(--brand)" }}>Continue <ChevronRight className="w-4 h-4" /></Button>}
              {step === 3 && <Button data-testid="onboard-finish" onClick={finish} disabled={busy} className="text-white gap-2" style={{ background: "var(--brand)" }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Enter LaunchDeck</Button>}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ShortcutCard({ title, desc, href }) {
  return (
    <a href={href} className="p-4 rounded-lg border block hover:-translate-y-0.5 transition-all" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>{desc}</div>
    </a>
  );
}
